import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {Stack} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState} from 'react'

import styles from './styles.module.css'

const TopNavContext = createContext<{
  hasLocalNavigation: boolean
  searchOpen: boolean
  isSearchPage: boolean
  setSearchOpen: (open: boolean) => void
  getFragmentsPath: (type: string) => string
  topNavHidden: boolean
  setTopNavHidden: (hidden: boolean) => void
}>({
  hasLocalNavigation: false,
  searchOpen: false,
  isSearchPage: false,
  setSearchOpen: () => {},
  getFragmentsPath: (type: string) => `/_global-navigation/fragments?type=${type}`,
  setTopNavHidden: () => {},
  topNavHidden: false,
})

export type TopNavProps = React.PropsWithChildren<{
  /** Whether the navigation has local navigation tabs below the main bar */
  hasLocalNavigation: boolean
  /** Whether the search input is on the search page */
  isSearchPage: boolean
  /** Whether the search input is a "jump to" search */
  isJumpToSearch: boolean
  /** Path to fetch html fragments from */
  searchFragmentsPath?: string
  /** Additional parameters to append to fragment requests */
  searchFragmentsParams?: string
}>

export function shouldHideTopNav(urlParams: URLSearchParams, isSearchPage: boolean, isJumpToSearch: boolean) {
  const shouldHide = isSearchPage && !isJumpToSearch && !urlParams.get('q')?.trim()

  return shouldHide
}

export function TopNavProvider({
  children,
  hasLocalNavigation,
  searchFragmentsPath,
  searchFragmentsParams,
  isSearchPage,
  isJumpToSearch,
}: TopNavProps) {
  const getFragmentsPath = useCallback(
    (type: string) => {
      return searchFragmentsPath
        ? `${searchFragmentsPath}?type=${type}&${searchFragmentsParams ?? ''}&is_search_page=${isSearchPage}`
        : `/_global-navigation/fragments?type=${type}&${searchFragmentsParams ?? ''}&is_search_page=${isSearchPage}`
    },
    [isSearchPage, searchFragmentsParams, searchFragmentsPath],
  )

  const [searchOpen, setSearchOpen] = useState(false)
  const [topNavHidden, setTopNavHidden] = useState(() => {
    const urlParams = new URLSearchParams(ssrSafeLocation.search)
    // This can be overridden later via the context setter,
    // but set the default here based on URL params so we don't flash the nav on initial load
    return shouldHideTopNav(urlParams, isSearchPage, isJumpToSearch)
  })

  const context = useMemo(
    () => ({
      hasLocalNavigation,
      searchOpen,
      isSearchPage,
      setSearchOpen,
      getFragmentsPath,
      setTopNavHidden,
      topNavHidden,
    }),
    [getFragmentsPath, hasLocalNavigation, isSearchPage, searchOpen, topNavHidden],
  )

  return <TopNavContext value={context}>{children}</TopNavContext>
}

/**
 * React hook to access the TopNav context.
 *
 * Returns an object with the following values:
 * - `hasLocalNavigation` (boolean): Whether local navigation tabs are present below the main bar.
 * - `searchOpen` (boolean): Whether the search input is currently open.
 * - `isSearchPage` (boolean): Whether the search input is on the search page.
 * - `setSearchOpen` (function): Function to set the `searchOpen` state.
 * - `getFragmentsPath` (function): Function to get the path for fetching HTML fragments based on type.
 *
 * Should be used within components that are descendants of `TopNavProvider` or `TopNav`.
 */
export function useTopNavContext() {
  return useContext(TopNavContext)
}

function TopNavComponent({
  children,
  className,
}: PropsWithChildren<{
  className?: string
}>) {
  const {topNavHidden} = useTopNavContext()

  const [{left, center, right, above}, rest] = useSlots(children, {
    left: Left,
    center: Center,
    right: Right,
    above: Above,
  })

  return (
    <Stack
      as="header"
      className={clsx(
        'GlobalNav',
        {
          'd-none': topNavHidden,
        },
        styles.appHeader,
        className,
      )}
      role="banner"
      aria-label="Global Navigation Menu"
      gap="none"
    >
      {above}
      <Stack direction="horizontal" align="center" justify="center">
        {left}
        {center}
        {right}
      </Stack>
      {rest}
    </Stack>
  )
}

function Left(props: React.PropsWithChildren) {
  const {hasLocalNavigation} = useContext(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-left"
      direction="horizontal"
      className={clsx(styles.left, hasLocalNavigation && styles.withLocalNavigation)}
      gap="condensed"
      padding="normal"
    >
      {props.children}
    </Stack>
  )
}

function Center(props: React.PropsWithChildren<{className?: string}>) {
  const {hasLocalNavigation} = useContext(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-center"
      direction="horizontal"
      gap="condensed"
      padding="normal"
      className={clsx(styles.center, hasLocalNavigation && styles.withLocalNavigation, props.className)}
    >
      {props.children}
    </Stack>
  )
}

function Right(props: React.PropsWithChildren) {
  const {hasLocalNavigation} = useContext(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-right"
      direction="horizontal"
      align="center"
      gap="condensed"
      padding="normal"
      className={clsx(styles.right, hasLocalNavigation && styles.withLocalNavigation)}
    >
      {props.children}
    </Stack>
  )
}

function Above(props: React.PropsWithChildren) {
  return <>{props.children}</>
}

/**
 * The TopNav component lays the structure for the top navigation bar within product.
 * Use the `TopNav.Above`, `TopNav.Left`, `TopNav.Center`, and `TopNav.Right` slots to define the content above, left, center, and right sides of the navigation bar.
 * - `TopNav.Above`: Content rendered above the main navigation bar (e.g., banners, alerts).
 * - `TopNav.Left`, `TopNav.Center`, `TopNav.Right`: Content rendered on the left, center, and right of the navigation bar, respectively.
 * Any additional children will be shown below the initial bar (such as Local Navigation).
 */
export const TopNav = Object.assign(TopNavComponent, {Above, Left, Center, Right})

try{ TopNavProvider.displayName ||= 'TopNavProvider' } catch {}
try{ TopNavComponent.displayName ||= 'TopNavComponent' } catch {}
try{ Left.displayName ||= 'Left' } catch {}
try{ Center.displayName ||= 'Center' } catch {}
try{ Right.displayName ||= 'Right' } catch {}
try{ Above.displayName ||= 'Above' } catch {}
try{ TopNav.displayName ||= 'TopNav' } catch {}